import React, { useEffect } from 'react';
import { X } from 'lucide-react';
export type LegalDocType = 'terms' | 'privacy';
interface LegalModalProps {
  isOpen: boolean;
  type: LegalDocType;
  onClose: () => void;
}
const TERMS_CONTENT = {
  title: 'Términos y Condiciones',
  intro: 'Al adquirir el curso "Arte con Cemento" aceptás los siguientes términos y condiciones. Te recomendamos leerlos con atención antes de realizar tu compra.',
  sections: [{
    heading: '1. Naturaleza del producto',
    body: 'Arte con Cemento es un producto digital de carácter educativo compuesto por videos, guías en PDF y materiales descargables. No incluye envío físico de ningún tipo. El acceso se entrega de forma 100% digital a través de la plataforma indicada en el correo de confirmación.'
  }, {
    heading: '2. Entrega y acceso',
    body: 'Una vez confirmado el pago, el acceso al contenido se envía al correo electrónico provisto durante la compra, habitualmente dentro de los primeros minutos. El acceso es de por vida e incluye futuras actualizaciones del programa principal.'
  }, {
    heading: '3. Precio y forma de pago',
    body: 'Los precios están expresados en pesos argentinos (ARS). Los pagos se procesan a través de Mercado Pago u otros medios habilitados, los cuales aplican sus propias políticas de seguridad y procesamiento. El precio puede variar según promociones vigentes al momento de la compra.'
  }, {
    heading: '4. Política de reembolso',
    body: 'Ofrecemos una garantía de satisfacción de 7 días corridos desde la fecha de compra. Si dentro de ese plazo considerás que el contenido no cumple con lo prometido, podés solicitar el reembolso del 100% del valor abonado escribiendo a nuestro canal oficial de contacto. Pasados los 7 días, no se aceptarán solicitudes de devolución.'
  }, {
    heading: '5. Uso del contenido',
    body: 'Todos los videos, textos, imágenes y materiales del curso son propiedad intelectual del autor. El contenido es para uso personal del comprador y no puede ser revendido, redistribuido, compartido, copiado, ni utilizado para crear cursos derivados sin autorización por escrito. La reproducción no autorizada puede dar lugar a acciones legales.'
  }, {
    heading: '6. Resultados',
    body: 'Si bien muchas de nuestras alumnas obtienen excelentes resultados, los mismos dependen del esfuerzo individual, la práctica y el seguimiento de las instrucciones. No garantizamos ingresos específicos ni resultados económicos derivados de la aplicación del contenido.'
  }, {
    heading: '7. Modificaciones',
    body: 'Nos reservamos el derecho de actualizar estos términos y condiciones en cualquier momento. La versión vigente será siempre la publicada en esta página al momento de la compra.'
  }]
};
const PRIVACY_CONTENT = {
  title: 'Políticas de Privacidad',
  intro: 'Tu privacidad es importante para nosotros. Esta política explica qué datos recolectamos, cómo los usamos y qué derechos tenés sobre ellos.',
  sections: [{
    heading: '1. Datos que recolectamos',
    body: 'Recolectamos información que vos nos proporcionás directamente al realizar una compra (nombre, correo electrónico, número de contacto y datos de facturación), así como datos de navegación obtenidos automáticamente mediante cookies y herramientas de analítica (páginas visitadas, dispositivo, ubicación aproximada).'
  }, {
    heading: '2. Cómo usamos tus datos',
    body: 'Utilizamos tus datos para procesar tu compra, enviarte el acceso al curso, brindarte soporte, mejorar nuestro servicio, enviarte comunicaciones relacionadas con el producto y, ocasionalmente, ofertas relevantes. Nunca vendemos ni cedemos tus datos personales a terceros con fines comerciales.'
  }, {
    heading: '3. Cookies y rastreo',
    body: 'Este sitio utiliza cookies propias y de terceros (Meta Pixel, Google Analytics y similares) para medir el comportamiento de los visitantes y optimizar nuestras campañas publicitarias. Podés deshabilitar las cookies desde la configuración de tu navegador.'
  }, {
    heading: '4. Almacenamiento y seguridad',
    body: 'Tus datos se almacenan en servidores seguros y solo el personal autorizado tiene acceso a ellos. Implementamos medidas técnicas razonables para protegerlos, aunque ningún sistema en internet es 100% infalible.'
  }, {
    heading: '5. Compartición con terceros',
    body: 'Compartimos datos únicamente con proveedores necesarios para operar el servicio (pasarela de pago, plataforma de envío de correos, herramientas de analítica). Todos los proveedores están obligados a tratar tus datos con confidencialidad.'
  }, {
    heading: '6. Tus derechos',
    body: 'Tenés derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos podés escribirnos a través de nuestros canales oficiales.'
  }, {
    heading: '7. Cambios en esta política',
    body: 'Esta política puede actualizarse en cualquier momento. La versión vigente será siempre la publicada en este sitio.'
  }]
};
export function LegalModal({
  isOpen,
  type,
  onClose
}: LegalModalProps) {
  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const doc = type === 'terms' ? TERMS_CONTENT : PRIVACY_CONTENT;
  return <div role="dialog" aria-modal="true" aria-labelledby="legal-title" className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 animate-fade-in-overlay" data-id="element-201">
      {/* Overlay */}
      <div className="absolute inset-0 bg-concrete-950/75 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-202" />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-sand-50 rounded-3xl shadow-premium overflow-hidden animate-fade-in-up max-h-[90vh] flex flex-col" data-id="element-203">
        {/* Header */}
        <div className="bg-concrete-900 text-sand-50 px-6 sm:px-10 py-6 flex items-start justify-between gap-4 border-b border-concrete-800 relative" data-id="element-204">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-gold-500/20 rounded-full blur-3xl pointer-events-none" data-id="element-205" />

          <div className="relative z-10" data-id="element-206">
            <p className="text-[10px] sm:text-xs tracking-widest uppercase text-gold-400 font-medium mb-1" data-id="element-207">
              Documento Legal
            </p>
            <h2 id="legal-title" className="font-heading text-2xl sm:text-3xl font-semibold leading-tight text-balance text-embossed" data-id="element-208">
              {doc.title}
            </h2>
          </div>

          <button onClick={onClose} aria-label="Cerrar" className="relative z-10 flex-shrink-0 p-2 rounded-full bg-sand-50/10 hover:bg-sand-50/20 text-sand-50 transition-colors" data-id="element-209">
            <X className="w-5 h-5" data-id="element-210" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto px-6 sm:px-10 py-8 flex-1" data-id="element-211">
          <p className="text-concrete-700 leading-relaxed mb-8 italic font-light" data-id="element-212">
            {doc.intro}
          </p>

          <div className="space-y-7" data-id="element-213">
            {doc.sections.map((s, i) => <section key={i} data-id="element-214">
                <h3 className="font-heading text-lg font-semibold text-concrete-900 mb-2" data-id="element-215">
                  {s.heading}
                </h3>
                <p className="text-sm md:text-base text-concrete-600 font-light leading-relaxed" data-id="element-216">
                  {s.body}
                </p>
              </section>)}
          </div>

          <p className="mt-10 pt-6 border-t border-sand-200 text-xs text-concrete-500 italic" data-id="element-217">
            Última actualización: {new Date().getFullYear()}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-10 py-4 bg-white border-t border-sand-200" data-id="element-218">
          <button onClick={onClose} className="w-full sm:w-auto sm:ml-auto block bg-terracotta-gradient text-white font-medium uppercase tracking-widest text-xs px-8 py-3 rounded-full hover:-translate-y-0.5 transition-transform shadow-sm" data-id="element-219">
            Entendido
          </button>
        </div>
      </div>
    </div>;
}